import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

const AUDIO_BASE_URL = 'https://media.satoshiadvance.com/audio';

const PLAYLIST = [
  `${AUDIO_BASE_URL}/retrodisco.mp3`,
  `${AUDIO_BASE_URL}/retrolightning.mp3`,
  `${AUDIO_BASE_URL}/retrotrance.mp3`,
  `${AUDIO_BASE_URL}/retrogame8.mp3`,
  `${AUDIO_BASE_URL}/UpbeatRetroIntro.mp3`,
  `${AUDIO_BASE_URL}/DogTunes7.mp3`,
  `${AUDIO_BASE_URL}/DogTunes1.mp3`,
  `${AUDIO_BASE_URL}/DogTunes2.mp3`,
  `${AUDIO_BASE_URL}/DogTunes3.mp3`,
  `${AUDIO_BASE_URL}/DogTunes4.mp3`,
  `${AUDIO_BASE_URL}/DogTunes5.mp3`,
  `${AUDIO_BASE_URL}/DogTunes6.mp3`,
  `${AUDIO_BASE_URL}/DogTunes8.mp3`,
  `${AUDIO_BASE_URL}/DogTunes9.mp3`,
  `${AUDIO_BASE_URL}/DogTunes10.mp3`,
  `${AUDIO_BASE_URL}/DogTunes11.mp3`,
  `${AUDIO_BASE_URL}/DogTunes12.mp3`,
  `${AUDIO_BASE_URL}/DogTunes13.mp3`,
  `${AUDIO_BASE_URL}/DogTunes14.mp3`,
  `${AUDIO_BASE_URL}/DogTunes15.mp3`
];

const MusicContext = createContext();

// A single, persistent background-music player. Because the <audio> element
// lives here (above the instructions screen and the in-game MusicPlayer),
// music keeps playing across the instructions -> game transition and across
// routes — nothing unmounts it.
export const MusicProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(
    () => Math.floor(Math.random() * PLAYLIST.length)
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);

  // When the track changes, load the new src and keep playing if we were.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    audio.src = PLAYLIST[currentTrackIndex];
    if (isPlaying) {
      audio.load();
      audio.play().catch((error) => console.log('Error playing audio:', error));
    }
    // isPlaying intentionally omitted: we only react to track changes here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrackIndex]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    audio
      .play()
      .then(() => setIsPlaying(true))
      .catch((error) => console.log('Error playing audio:', error));
  }, []);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    audio.pause();
    setIsPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  const skip = useCallback(() => {
    setCurrentTrackIndex((i) => (i < PLAYLIST.length - 1 ? i + 1 : 0));
  }, []);

  const toggleVolume = useCallback(() => {
    setVolume((v) => (v === 1 ? 0.33 : 1));
  }, []);

  // When a track finishes, advance through the playlist (the track-change
  // effect above resumes playback automatically since isPlaying is true).
  const handleEnded = useCallback(() => {
    setCurrentTrackIndex((i) => (i < PLAYLIST.length - 1 ? i + 1 : 0));
  }, []);

  // Skip past tracks that fail to load (404, decode error) so one bad file
  // doesn't silently kill the music. Stops after one full failed cycle.
  const failedTracksRef = useRef(0);
  const handleError = useCallback(() => {
    failedTracksRef.current += 1;
    if (failedTracksRef.current >= PLAYLIST.length) return;
    setCurrentTrackIndex((i) => (i < PLAYLIST.length - 1 ? i + 1 : 0));
  }, []);
  const handleCanPlay = useCallback(() => {
    failedTracksRef.current = 0;
  }, []);

  const value = useMemo(
    () => ({ isPlaying, volume, play, pause, toggle, skip, toggleVolume }),
    [isPlaying, volume, play, pause, toggle, skip, toggleVolume]
  );

  return (
    <MusicContext.Provider value={value}>
      <audio
        ref={audioRef}
        src={PLAYLIST[currentTrackIndex]}
        preload="none"
        onEnded={handleEnded}
        onError={handleError}
        onCanPlay={handleCanPlay}
      />
      {children}
    </MusicContext.Provider>
  );
};

export const useMusic = () => {
  const context = useContext(MusicContext);
  if (!context) {
    throw new Error('useMusic must be used within a MusicProvider');
  }
  return context;
};

export default MusicContext;
